import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { User, Building2, LifeBuoy, ShieldAlert, Download, Trash2, KeyRound, Copy, Plus } from "lucide-react";
import { useData } from "@/context/DataContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import client from "@/lib/api";
import { toast } from "sonner";

const ORG_TYPES = ["Entreprise", "Agence événementielle", "Collectivité", "Université / Centre de formation", "Association"];
const ROLES = ["Auto-entrepreneur", "Fondateur", "CEO", "Président", "DG", "Responsable Événementiel", "Chargé de Communication", "Responsable Marketing", "DAF", "Autre"];

function SectionCard({ icon: Icon, title, children, danger }) {
  return (
    <div className={`bg-card rounded-xl border p-6 ${danger ? "border-rose-200" : "border-border"}`}>
      <h3 className={`font-display font-semibold mb-5 flex items-center gap-2 ${danger ? "text-rose-600" : ""}`}><Icon className={`h-4 w-4 ${danger ? "text-rose-600" : "text-indigo-600"}`} />{title}</h3>
      {children}
    </div>
  );
}
function F({ label, children, className = "" }) { return (<div className={className}><Label className="mb-1.5 block">{label}</Label>{children}</div>); }

export default function Settings() {
  const { profile, company, saveProfile, saveCompany } = useData();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [p, setP] = useState({ firstName: "", lastName: "", email: "", phone: "", role: "" });
  const [c, setC] = useState({ companyName: "", orgType: "", siret: "", vat: "", headquartersAddress: "", headquartersCity: "", headquartersPostal: "", bankHolder: "", bankName: "", iban: "", bic: "" });
  const [support, setSupport] = useState({ kind: "client", type: "", subject: "", message: "" });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [delPassword, setDelPassword] = useState("");
  const [codes, setCodes] = useState(null);

  useEffect(() => { client.get("/admin/signup-codes").then((r) => setCodes(r.data)).catch(() => setCodes(null)); }, []);
  const genCode = async () => { try { const { data } = await client.post("/admin/signup-codes", { maxUses: 0 }); setCodes((c) => [data, ...(c || [])]); toast.success("Code généré"); } catch { toast.error("Génération impossible"); } };
  const toggleCode = async (code, active) => { try { await client.post(`/admin/signup-codes/${code}/${active ? "revoke" : "activate"}`); setCodes((c) => c.map((x) => x.code === code ? { ...x, active: !active } : x)); } catch { toast.error("Action impossible"); } };
  const delCode = async (code) => { try { await client.delete(`/admin/signup-codes/${code}`); setCodes((c) => c.filter((x) => x.code !== code)); toast.success("Code supprimé"); } catch { toast.error("Suppression impossible"); } };

  useEffect(() => {
    setP((v) => ({ ...v, ...profile, email: profile.email || user?.email || "" }));
    setC((v) => ({ ...v, ...company }));
  }, [profile, company, user]);

  const sendSupport = async () => {
    try {
      await client.post("/support", { kind: support.kind, type: support.type, subject: support.subject, message: support.message });
      toast.success("Demande envoyée à notre équipe (sav@justplanit.fr)"); setSupport({ ...support, subject: "", message: "" });
    } catch {
      toast.error("Envoi impossible, réessayez plus tard");
    }
  };

  const downloadData = async () => {
    const { data } = await client.get("/account/export");
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "justplanit-donnees.json"; a.click();
    toast.success("Vos données ont été téléchargées");
  };

  const deleteAccount = async () => {
    try {
      await client.post("/account/delete", { password: delPassword });
      toast.success("Compte supprimé");
      logout(); navigate("/");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suppression impossible");
    }
  };

  return (
    <>
      <PageHeader title="Paramètres" subtitle="Gérez votre compte et votre organisme" />
      <div className="space-y-6">
        <SectionCard icon={User} title="Profil personnel">
          <div className="grid sm:grid-cols-2 gap-4">
            <F label="Prénom"><Input data-testid="set-firstname" value={p.firstName} onChange={(e) => setP({ ...p, firstName: e.target.value })} /></F>
            <F label="Nom"><Input data-testid="set-lastname" value={p.lastName} onChange={(e) => setP({ ...p, lastName: e.target.value })} /></F>
            <F label="Email"><Input data-testid="set-email" value={p.email} onChange={(e) => setP({ ...p, email: e.target.value })} /></F>
            <F label="Téléphone"><Input data-testid="set-phone" value={p.phone} onChange={(e) => setP({ ...p, phone: e.target.value })} /></F>
            <F label="Rôle"><Select value={p.role} onValueChange={(v) => setP({ ...p, role: v })}><SelectTrigger data-testid="set-role"><SelectValue placeholder="Sélectionner" /></SelectTrigger><SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent></Select></F>
          </div>
          <div className="flex justify-end mt-4"><Button onClick={async () => { await saveProfile(p); toast.success("Profil mis à jour"); }} data-testid="save-profile">Mettre à jour</Button></div>
        </SectionCard>

        <SectionCard icon={Building2} title="Organisme">
          <div className="grid sm:grid-cols-2 gap-4">
            <F label="Raison sociale"><Input data-testid="set-company" value={c.companyName} onChange={(e) => setC({ ...c, companyName: e.target.value })} /></F>
            <F label="Type d'organisme"><Select value={c.orgType} onValueChange={(v) => setC({ ...c, orgType: v })}><SelectTrigger data-testid="set-orgtype"><SelectValue placeholder="Sélectionner" /></SelectTrigger><SelectContent>{ORG_TYPES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent></Select></F>
            <F label="SIRET"><Input value={c.siret} onChange={(e) => setC({ ...c, siret: e.target.value })} /></F>
            <F label="N° TVA"><Input value={c.vat} onChange={(e) => setC({ ...c, vat: e.target.value })} /></F>
            <F label="Adresse" className="sm:col-span-2"><Input value={c.headquartersAddress} onChange={(e) => setC({ ...c, headquartersAddress: e.target.value })} /></F>
            <F label="Ville"><Input value={c.headquartersCity} onChange={(e) => setC({ ...c, headquartersCity: e.target.value })} /></F>
            <F label="Code postal"><Input value={c.headquartersPostal} onChange={(e) => setC({ ...c, headquartersPostal: e.target.value })} /></F>
          </div>
          <div className="mt-5 pt-5 border-t border-border">
            <p className="text-sm font-semibold mb-3 flex items-center gap-2">🏦 Coordonnées bancaires <span className="text-[10px] font-normal text-muted-foreground">(pour recevoir les paiements Stripe & virements)</span></p>
            <div className="grid sm:grid-cols-2 gap-4">
              <F label="Titulaire du compte"><Input data-testid="set-bank-holder" value={c.bankHolder || ""} onChange={(e) => setC({ ...c, bankHolder: e.target.value })} placeholder="Nom exact figurant sur le RIB" /></F>
              <F label="Banque"><Input data-testid="set-bank-name" value={c.bankName || ""} onChange={(e) => setC({ ...c, bankName: e.target.value })} placeholder="BNP Paribas, Crédit Agricole…" /></F>
              <F label="IBAN" className="sm:col-span-2"><Input data-testid="set-iban" value={c.iban || ""} onChange={(e) => setC({ ...c, iban: e.target.value.toUpperCase() })} placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX" className="font-mono" /></F>
              <F label="BIC / SWIFT"><Input data-testid="set-bic" value={c.bic || ""} onChange={(e) => setC({ ...c, bic: e.target.value.toUpperCase() })} placeholder="BNPAFRPPXXX" className="font-mono" /></F>
            </div>
          </div>
          <div className="flex justify-end mt-4"><Button onClick={async () => { await saveCompany(c); toast.success("Organisme mis à jour"); }} data-testid="save-company">Mettre à jour</Button></div>
        </SectionCard>

        <SectionCard icon={LifeBuoy} title="Support">
          <div className="flex gap-2 mb-4">
            <Button variant={support.kind === "client" ? "default" : "outline"} size="sm" onClick={() => setSupport({ ...support, kind: "client" })} data-testid="support-client">Service client</Button>
            <Button variant={support.kind === "tech" ? "default" : "outline"} size="sm" onClick={() => setSupport({ ...support, kind: "tech" })} data-testid="support-tech">Incident technique</Button>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <F label="Type de demande"><Input value={support.type} onChange={(e) => setSupport({ ...support, type: e.target.value })} /></F>
            <F label="Sujet"><Input value={support.subject} onChange={(e) => setSupport({ ...support, subject: e.target.value })} /></F>
            <F label="Message" className="sm:col-span-2"><Textarea rows={3} value={support.message} onChange={(e) => setSupport({ ...support, message: e.target.value })} /></F>
          </div>
          {support.kind === "tech" && <p className="text-xs text-muted-foreground mt-2">Infos système jointes automatiquement : {navigator.platform} · {new Date().toLocaleString("fr-FR")}</p>}
          <div className="flex justify-end mt-4"><Button onClick={sendSupport} data-testid="send-support">Envoyer</Button></div>
        </SectionCard>

        {codes !== null && (
          <SectionCard icon={KeyRound} title="Codes d'accès (inscription)">
            <p className="text-sm text-muted-foreground mb-4">Seules les personnes disposant d'un code actif peuvent créer un compte. Générez et partagez des codes avec vos clients.</p>
            <Button size="sm" onClick={genCode} data-testid="generate-code" className="mb-4"><Plus className="h-4 w-4 mr-1" />Générer un code</Button>
            <div className="space-y-2">
              {codes.length ? codes.map((c) => (
                <div key={c.code} className="flex items-center gap-2 flex-wrap border border-border rounded-lg p-2.5" data-testid={`code-${c.code}`}>
                  <code className="font-mono font-semibold">{c.code}</code>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${c.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`}>{c.active ? "Actif" : "Révoqué"}</span>
                  <span className="text-xs text-muted-foreground">{c.uses} utilisation(s){c.maxUses ? ` / ${c.maxUses} max` : ""}</span>
                  <div className="ml-auto flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => { navigator.clipboard?.writeText(c.code); toast.success("Code copié"); }} data-testid={`copy-code-${c.code}`}><Copy className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" variant="outline" onClick={() => toggleCode(c.code, c.active)} data-testid={`toggle-code-${c.code}`}>{c.active ? "Révoquer" : "Réactiver"}</Button>
                    <Button size="sm" variant="ghost" onClick={() => delCode(c.code)}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
                  </div>
                </div>
              )) : <p className="text-sm text-muted-foreground">Aucun code. Générez-en un pour autoriser des inscriptions.</p>}
            </div>
          </SectionCard>
        )}

        <SectionCard icon={ShieldAlert} title="Mon compte" danger>
          <p className="text-sm text-muted-foreground mb-4">Téléchargez l'ensemble de vos données ou supprimez définitivement votre compte.</p>
          <div className="flex gap-3 flex-wrap">
            <Button variant="outline" onClick={downloadData} data-testid="download-data"><Download className="h-4 w-4 mr-2" />Télécharger mes données</Button>
            <Button variant="destructive" onClick={() => setConfirmOpen(true)} data-testid="delete-account"><Trash2 className="h-4 w-4 mr-2" />Supprimer mon compte</Button>
          </div>
        </SectionCard>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Supprimer définitivement votre compte ?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Cette action est irréversible. Tous vos événements, tâches, invités et données seront supprimés. Pensez à télécharger vos données avant.</p>
          <div className="mt-4">
            <Label className="mb-1.5 block">Saisissez votre mot de passe pour confirmer</Label>
            <Input type="password" value={delPassword} onChange={(e) => setDelPassword(e.target.value)} data-testid="delete-password" placeholder="Mot de passe" />
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => { setConfirmOpen(false); setDelPassword(""); }}>Annuler</Button>
            <Button variant="destructive" disabled={!delPassword} onClick={deleteAccount} data-testid="confirm-delete-account">Supprimer définitivement</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
